// Map frontend module keys to master server permission names from GET /admin/list/permission
const PERMISSION_ALIASES = {
  "products": ["product management", "products", "product"],
  "orders": ["order management", "orders", "order"],
  "user management": [
    "sales user and customer management",
    "user management",
    "sales users",
    "sales user",
    "customers",
    "customer",
    "user groups",
    "user group",
    "sub admins",
    "sub admin"
  ],
  "sub admins": ["sales user and customer management", "sub admins", "sub admin"],
  "sales users": ["sales user and customer management", "sales users", "sales user"],
  "customers": ["sales user and customer management", "customers", "customer"],
  "user groups": ["sales user and customer management", "user groups", "user group"],
  "promotion management": ["promotion management", "offer management"],
  "membership management": ["member management", "membership management"],
  "memberships": ["member management", "memberships", "membership"],
  "story": ["notification management", "story"],
  "dashboard banners": ["notification management", "dashboard banners", "banners"],
  "ai notifications": ["notification management", "ai notifications", "notifications"],
  "category fields": ["notification management", "category fields"],
  "chats": ["chat", "chats"],
};

/**
 * Checks if the logged-in user has permission to access a specific module or feature.
 *
 * @param {Object} user - User object from Redux state or localStorage (userData)
 * @param {string|number} requiredPermission - Name or ID of the required permission (e.g. "Products", "Orders", "Sub admins")
 * @param {Array} masterPermissions - List of all system permissions fetched from GET /admin/list/permission
 * @returns {boolean} - true if user is super admin or holds required permission, false otherwise
 */
export const hasModulePermission = (user, requiredPermission, masterPermissions = []) => {
  if (!user) return false;

  // Only Sub-Admins (usertype: sub_admin / subadmin / sub-admin or having sub_admin_permissions) have module restrictions
  const userTypeStr = String(user.usertype || user.user_type || "").toLowerCase().trim();
  const isSubAdmin =
    userTypeStr === "subadmin" ||
    userTypeStr === "sub_admin" ||
    userTypeStr === "sub-admin" ||
    Boolean(user.sub_admin_permissions && user.sub_admin_permissions.length > 0) ||
    Boolean(user.permission) ||
    Boolean(user.permissions && user.permissions.length > 0);

  // Super Admins, Sales Users, and non-sub-admins have full module access
  if (!isSubAdmin) {
    return true;
  }


  // Dashboard & Profile are accessible to all authenticated users
  if (!requiredPermission || requiredPermission === "Dashboard") {
    return true;
  }

  const reqKey = String(requiredPermission).toLowerCase().trim();
  const targetAliases = PERMISSION_ALIASES[reqKey] || [reqKey];

  // Extract assigned permission IDs and names from user object
  let assignedIds = [];
  let assignedNames = [];

  if (Array.isArray(user.sub_admin_permissions)) {
    user.sub_admin_permissions.forEach((p) => {
      if (p && typeof p === "object") {
        if (p.id !== undefined) assignedIds.push(String(p.id));
        if (p.name) assignedNames.push(String(p.name).toLowerCase().trim());
      } else if (p !== undefined) {
        assignedIds.push(String(p));
      }
    });
  } else if (Array.isArray(user.permissions)) {
    user.permissions.forEach((p) => {
      if (p && typeof p === "object") {
        if (p.id !== undefined) assignedIds.push(String(p.id));
        if (p.name) assignedNames.push(String(p.name).toLowerCase().trim());
      } else if (p !== undefined) {
        assignedIds.push(String(p));
      }
    });
  } else if (typeof user.permission === "string" && user.permission.trim().length > 0) {
    assignedIds = user.permission.split(",").map((s) => s.trim());
  }

  // 1. Check direct name matches against aliases
  if (assignedNames.some((name) => targetAliases.includes(name))) {
    return true;
  }

  // 2. Check direct ID matches if requiredPermission is passed as ID
  if (assignedIds.includes(String(requiredPermission))) {
    return true;
  }

  // 3. Resolve assigned IDs using masterPermissions list from server
  if (masterPermissions && masterPermissions.length > 0) {
    // Find master permission IDs that match any of our target aliases
    const targetMasterIds = masterPermissions
      .filter((m) => targetAliases.includes(String(m?.name).toLowerCase().trim()))
      .map((m) => String(m.id));

    // If any assigned ID matches target master permission IDs
    if (assignedIds.some((id) => targetMasterIds.includes(String(id)))) {
      return true;
    }
  }

  return false;
};

